import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import adminApi from '../../../services/adminApi';
import { Badge } from '../../../components/ui/badge';
import Table from '../../../components/admin/common/Table';

const CustomerDetailsPage = () => {
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id } = useParams();

  useEffect(() => {
    const fetchDetails = async () => {
      setIsLoading(true);
      try {
        const response = await adminApi.getCustomerDetails(id);
        if (response.success) {
          setDetails(response.details);
        } else {
          setError(response.error || 'Failed to fetch customer details');
        }
      } catch (err) {
        setError(err.toString());
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const orderColumns = [
    { key: '_id', header: 'Order ID' },
    { key: 'createdAt', header: 'Date', render: (order) => new Date(order.createdAt).toLocaleDateString() },
    { key: 'totalPrice', header: 'Total', render: (order) => `£${order.totalPrice.toFixed(2)}` },
    { key: 'isPaid', header: 'Paid', render: (order) => (
        <Badge variant={order.isPaid ? 'success' : 'danger'}>{order.isPaid ? 'Yes' : 'No'}</Badge>
    )},
    { key: 'orderStatus', header: 'Status', render: (order) => (
        <Badge>{order.orderStatus}</Badge>
    )},
  ];

  const coinTransactionColumns = [
    { key: 'createdAt', header: 'Date', render: (item) => new Date(item.createdAt).toLocaleString() },
    { key: 'description', header: 'Description' },
    { key: 'amount', header: 'Amount', render: (item) => (
        <span className={item.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
            {item.type === 'credit' ? '+' : '-'}{item.amount}
        </span>
    )},
    { key: 'status', header: 'Status', render: (item) => (
        <Badge variant={item.status === 'completed' ? 'success' : 'warning'}>{item.status}</Badge>
    )},
  ];

  const loginHistoryColumns = [
    { key: 'loggedInAt', header: 'Date', render: (item) => new Date(item.loggedInAt).toLocaleString() },
    { key: 'ip', header: 'IP Address' },
  ];

  if (isLoading) {
    return <div className="p-6">Loading customer details...</div>;
  }

  if (error) {
    return <div className="p-6 bg-red-100 text-red-700">Error: {error}</div>;
  }

  if (!details) {
    return <div className="p-6">No details found for this customer.</div>;
  }

  const { user, orders, totalSpent } = details;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Customer Details</h1>
        <Link to="/admin/customers" className="text-blue-600 hover:underline">
          &larr; Back to Customers
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - User Info */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">{user.firstName} {user.lastName}</h2>
            <p className="text-sm text-gray-600">{user.email}</p>
            <p className="text-sm text-gray-600">{user.phone || 'No phone number'}</p>
            <Badge variant={user.isVerified ? 'success' : 'danger'} className="mt-2">
              {user.isVerified ? 'Verified' : 'Not Verified'}
            </Badge>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">Stats</h2>
            <p>Total Spent: <span className="font-bold">£{totalSpent.toFixed(2)}</span></p>
            <p>Total Orders: <span className="font-bold">{orders.length}</span></p>
            <p>Member Since: <span className="font-bold">{new Date(user.createdAt).toLocaleDateString()}</span></p>
            <p>MAD Coins: <span className="font-bold">{user.madCoins?.balance || 0}</span></p>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">Last Login</h2>
            <p>{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}</p>
            <p>IP: {user.lastLoginIp || 'N/A'}</p>
          </div>
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">Order History</h2>
            <Table columns={orderColumns} data={orders} emptyMessage="No orders found." />
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">Login History</h2>
            <Table columns={loginHistoryColumns} data={user.loginHistory} emptyMessage="No login history found." />
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">MAD Coin History</h2>
            <Table columns={coinTransactionColumns} data={user.madCoins?.transactions || []} emptyMessage="No coin transactions found." />
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h2 className="text-lg font-semibold mb-2">Addresses</h2>
            {user.addresses && user.addresses.length > 0 ? (
              user.addresses.map((address, index) => (
                <div key={index} className="mb-2 p-2 border-b">
                  <p className="font-bold">{address.isDefault ? 'Default Address' : `Address ${index + 1}`}</p>
                  <p>{address.addressLine1}</p>
                  {address.addressLine2 && <p>{address.addressLine2}</p>}
                  <p>{address.townCity}, {address.postcode}</p>
                </div>
              ))
            ) : (
              <p>No addresses found.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsPage;
